interface NewsApiArticle {
  source: {
    id: string | null;
    name: string;
  };
  author: string | null;
  title: string;
  description: string;
  url: string;
  urlToImage: string | null;
  publishedAt: string;
  content: string;
}

interface NewsApiResponse {
  status: string;
  totalResults: number;
  articles: NewsApiArticle[];
}

export interface FormattedNewsArticle {
  id: string;
  title: string;
  summary: string;
  image: string;
  imageAlt: string;
  source: {
    name: string;
    logo: string;
    logoAlt: string;
    url: string;
  };
  category: string;
  timestamp: string;
  readTime: string;
  country: string;
  publishedAt: string;
  views?: number;
}

const NEWS_API_KEY = process.env.NEWS_API_KEY || '';
const NEWS_API_BASE_URL = 'https://newsapi.org/v2';

// Country code mapping for News API
const COUNTRY_CODE_MAP: Record<string, string> = {
  'US': 'us',
  'IN': 'in',
  'GB': 'gb',
  'CN': 'cn',
  'ID': 'id',
  'PK': 'pk',
  'BR': 'br',
  'NG': 'ng',
  'BD': 'bd',
  'RU': 'ru',
  'MX': 'mx',
  'JP': 'jp',
  'PH': 'ph',
  'EG': 'eg',
  'VN': 'vn',
  'TR': 'tr',
  'DE': 'de',
  'FR': 'fr',
  'TH': 'th',
  'IT': 'it',
};

// Default logo for news sources
const DEFAULT_SOURCE_LOGO = 'https://img.rocket.new/generatedImages/rocket_gen_img_1a6486767-1770656497074.png';

/**
 * Fetch country-specific news from News API
 * Articles are sorted by:
 * 1. Relevance (based on News API's relevancy score)
 * 2. Publication date (most recent first)
 */
export async function fetchCountryNews(
  countryCode: string,
  category?: string,
  pageSize: number = 20
): Promise<FormattedNewsArticle[]> {
  try {
    // Check if API key is configured
    if (!NEWS_API_KEY || NEWS_API_KEY === 'your-newsapi-key-here') {
      console.warn('News API key not configured, returning empty array');
      return [];
    }

    // For WORLD view, fetch top headlines from multiple countries
    if (countryCode === 'WORLD' || countryCode === 'all') {
      return await fetchWorldNews(category, pageSize);
    }

    // Map country code to News API format
    const apiCountryCode = COUNTRY_CODE_MAP[countryCode.toUpperCase()];
    
    if (!apiCountryCode) {
      console.warn(`Country code ${countryCode} not supported by News API`);
      return [];
    }

    // Build API URL
    let url = `${NEWS_API_BASE_URL}/top-headlines?country=${apiCountryCode}&pageSize=${pageSize}&apiKey=${NEWS_API_KEY}`;
    
    if (category && category !== 'all') {
      url += `&category=${category.toLowerCase()}`;
    }

    const response = await fetch(url);
    
    if (!response.ok) {
      console.error('News API error:', response.status, response.statusText);
      return [];
    }

    const data: NewsApiResponse = await response.json();

    if (data.status !== 'ok' || !data.articles) {
      console.error('Invalid News API response:', data);
      return [];
    }

    // Format and sort articles
    const formattedArticles = data.articles
      .filter(article => article.title && article.title !== '[Removed]')
      .map((article, index) => formatNewsArticle(article, countryCode, index));

    // Sort by publication date (most recent first)
    formattedArticles.sort((a, b) => {
      const dateA = new Date(a.publishedAt).getTime();
      const dateB = new Date(b.publishedAt).getTime();
      return dateB - dateA;
    });

    return formattedArticles;
  } catch (error) {
    console.error('Error fetching news from News API:', error);
    return [];
  }
}

/**
 * Fetch world news from multiple countries
 */
async function fetchWorldNews(
  category?: string,
  pageSize: number = 20
): Promise<FormattedNewsArticle[]> {
  try {
    if (!NEWS_API_KEY || NEWS_API_KEY === 'your-newsapi-key-here') {
      return [];
    }

    // Fetch from ALL supported countries in COUNTRY_CODE_MAP for comprehensive global coverage
    const countries = Object.values(COUNTRY_CODE_MAP);
    const allArticles: FormattedNewsArticle[] = [];

    // Fetch 2 articles from each country to get diverse global coverage
    const articlesPerCountry = Math.max(2, Math.ceil(pageSize / countries.length));

    for (const country of countries) {
      let url = `${NEWS_API_BASE_URL}/top-headlines?country=${country}&pageSize=${articlesPerCountry}&apiKey=${NEWS_API_KEY}`;
      
      if (category && category !== 'all') {
        url += `&category=${category.toLowerCase()}`;
      }

      try {
        const response = await fetch(url);
        if (response.ok) {
          const data: NewsApiResponse = await response.json();
          if (data.status === 'ok' && data.articles) {
            const formatted = data.articles
              .filter(article => article.title && article.title !== '[Removed]')
              .map((article, index) => formatNewsArticle(article, 'WORLD', index));
            allArticles.push(...formatted);
          }
        }
      } catch (err) {
        console.error(`Error fetching news from ${country}:`, err);
      }
    }

    // Sort by publication date and limit results
    allArticles.sort((a, b) => {
      const dateA = new Date(a.publishedAt).getTime();
      const dateB = new Date(b.publishedAt).getTime();
      return dateB - dateA;
    });

    return allArticles.slice(0, pageSize);
  } catch (error) {
    console.error('Error fetching world news:', error);
    return [];
  }
}

/**
 * Format News API article to match application format
 */
function formatNewsArticle(
  article: NewsApiArticle,
  countryCode: string,
  index: number
): FormattedNewsArticle {
  const publishedDate = new Date(article.publishedAt);
  const now = new Date();
  const diffMs = now.getTime() - publishedDate.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  let timestamp: string;
  if (diffMins < 60) {
    timestamp = `${diffMins} mins ago`;
  } else if (diffHours < 24) {
    timestamp = `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  } else {
    timestamp = `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  }

  // Determine category from article content
  const category = determineCategoryFromContent(article);

  // Generate unique ID
  const id = `newsapi_${countryCode}_${Date.now()}_${index}`;

  return {
    id,
    title: article.title,
    summary: article.description || article.content?.substring(0, 200) || 'No summary available',
    image: article.urlToImage || 'https://images.unsplash.com/photo-1504137013456-40118f92cac3',
    imageAlt: `News image for: ${article.title}`,
    source: {
      name: article.source.name,
      logo: DEFAULT_SOURCE_LOGO,
      logoAlt: `${article.source.name} logo`,
      url: article.url,
    },
    category,
    timestamp,
    readTime: '1 min',
    country: countryCode,
    publishedAt: article.publishedAt,
    views: Math.floor(Math.random() * 1000000) + 100000,
  };
}

/**
 * Determine article category from content
 */
function determineCategoryFromContent(article: NewsApiArticle): string {
  const text = `${article.title} ${article.description || ''}`.toLowerCase();

  if (text.match(/\b(tech|technology|ai|software|app|digital|cyber|internet)\b/)) {
    return 'Technology';
  }
  if (text.match(/\b(business|economy|market|stock|finance|trade|company)\b/)) {
    return 'Business';
  }
  if (text.match(/\b(sport|football|cricket|basketball|tennis|olympic)\b/)) {
    return 'Sports';
  }
  if (text.match(/\b(health|medical|doctor|hospital|disease|vaccine)\b/)) {
    return 'Health';
  }
  if (text.match(/\b(entertainment|movie|music|celebrity|film|actor)\b/)) {
    return 'Entertainment';
  }
  if (text.match(/\b(science|research|study|scientist|discovery)\b/)) {
    return 'Science';
  }
  if (text.match(/\b(politics|government|election|minister|president|parliament)\b/)) {
    return 'Politics';
  }

  return 'General';
}
